import { KnovioLabel } from '../../api/connections.dao';
import { DefaultCarouselView, SortOrganizationsByOptions } from '../../api/custom-pages.dao';
import {
    generalSessionsNotesInfoMessage,
    linkDescriptionInfoMessages,
    myScheduleNotesInfoMessage,
    replaysNotesInfoMessage,
    sortOrganizationsByInfoMessages
} from '../../constants/passport-pages';
import { FormVmItemConfigType, FormVmValueValidatorResultType } from '../../infrastructure/form-viewmodel-controller';
import { trimSpaces, trimSpacesLeft } from '../formatters';
import { file, InputValidatorType, plainText, requiredIdValidator, requiredIntegerValidator, requiredValidator, richText } from '../validators';

/* DTO Keys */
export const TYPE = 'type';
export const CUSTOM_PAGE_ID = 'customPageId';
export const LABEL = 'label';
export const LANDING_PAGE = 'landingPage';
export const ORDER = 'order';
export const PATH = 'path';
export const ENABLED = 'enabled';
export const PUBLIC_ENABLED = 'publicEnabled';
export const EMBED_CODE_ID = 'embedCodeId';
export const EMBEDDED_WEBPAGE_URL = 'embeddedWebpageUrl';
export const RICH_TEXT = 'richText';
export const MEETING_ID = 'meetingId';
export const EMBEDDED_ZIP_FILE = 'embeddedZIPFile';
export const EMBEDDED_HTML_FILE_OBJECT_KEY = 'embeddedHTMLFileObjectKey';
export const LINK_DESCRIPTION = 'linkDescription';
export const SUPPORT_INFORMATION = 'supportInformation';
export const WELCOME_KNOVIO_ENABLED = 'welcomeKnovioEnabled';
export const WELCOME_KNOVIO_EMBED_ID = 'welcomeKnovioEmbedId';
export const CALENDAR_BUTTON_ENABLED = 'calendarButtonEnabled';
export const LAYOUT = 'layout';
export const DEFAULT_CAROUSEL_VIEW = 'defaultCarouselView';
export const UPCOMING_SESSIONS_TYPE = 'upcomingSessionType';
export const USER_SHOWCASE = 'userShowcase';
export const EXTERNAL_LINK_URL = 'externalLink';
export const RESTRICT_ACCESS_ENABLED = 'restrictAccessEnabled';
export const RESTRICT_ACCESS_FILTERS = 'restrictAccessFilters';
export const PRE_REGISTRATION_ENABLED = 'preRegistrationEnabled';
export const PAST_SESSIONS_ENABLED = 'pastSessionsEnabled';
export const SUBSCRIBE_ATTENDEE_TO_MEETING_ON_REGISTRATION_ENABLED = 'subscribeAttendeeToMeetingOnRegistrationEnabled';
export const SORT_ORGANIZATION_BY = 'sortOrganizationsBy';
export const UPCOMING_SESSIONS_ENABLED = 'upcomingSessionsEnabled';
export const HOST_ATTENDEES_ENABLED = 'hostAttendeesEnabled';
export const UPCOMING_SESSIONS_LABEL = 'upcomingSessionsLabel';
export const REPLAYS_SESSION_LABEL = 'replaysSessionLabel';
export const REPLAYS_PRE_REGISTRATION_ENABLED = 'replaysPreRegistrationEnabled';
export const REPLAYS_LAYOUT = 'replaysLayout';
export const REPLAYS_DEFAULT_CAROUSEL_VIEW = 'replaysDefaultCarouselView';
export const PRESENTER_GROUPS_ENABLED = 'presentersFilterEnabled';
export const PRESENTER_GROUPS_FILTERS = 'selectedMeetingIds';
export const PRESENTER_GROUPS = 'presenterGroups';

export const TOPIC_ENABLED = 'topicEnabled';
export const PAGE_LINK_ENABLED = 'pageLinkEnabled';
export const MY_SCHEDULE_NOTES = 'scheduleNotes';
export const REPLAYS_NOTES = 'replaysNotes';
export const GENERAL_SESSIONS_NOTES = 'sessionNotes';
export const REGISTER_BUTTON_LABEL = 'registerButtonLabel';

export const RESERVED_PATHS = [
    'presenter',
    'participant',
    '404',
    'welcome',
    'agenda',
    'session',
    'stream',
    'showcase',
    'speakers',
    'replays',
    'support',
    'registration',
    'public',
    'google-event'
];

export const reservedPathValidator: InputValidatorType = (val: string = ''): FormVmValueValidatorResultType => {
    const idx = RESERVED_PATHS.indexOf(val);
    const valid = idx === -1;
    const validationMessage = valid
        ? null
        : {
            id: 'oe.validation.error.pagePathReserved',
            defaultMessage: 'The path {pathValue} is reserved, please choose another Page Path',
            values: { pathValue: `"${RESERVED_PATHS[idx]}"` }
        };
    return { valid: valid, validationMessage: validationMessage };
};

export const pagePathSymbolsValidator = (val: string = ''): FormVmValueValidatorResultType => {
    const valid = /^[a-z0-9]+(-?[a-z0-9])*$/.test(val);
    const validationMessage = valid
        ? null
        : {
            id: 'oe.validation.error.pagePathContainsInvalidChars',
            defaultMessage: '' +
                '- The Page Path must only contain valid characters: a-z (lowercase only), 0-9, and hyphen/dash ("-").' +
                ' \n ' +
                '- The Page Path must not begin or end with a hyphen/dash.'
        };

    return { valid: valid, validationMessage: validationMessage };
};

export const urlValidator = (val: string): FormVmValueValidatorResultType => {
    const valid = /^(https?:\/\/.+)/.test(val);
    const validationMessage = valid
        ? null
        : {
            id: 'oe.validation.error.embeddedWebpageUrl',
            defaultMessage: 'Please enter a valid URL beginning with http:// or https://'
        };

    return { valid: valid, validationMessage: validationMessage };
};

export const pagePathValidatorComposer = (isExistedPath: (value: string) => boolean): FormVmItemConfigType[] => {
    return [{
        name: PATH,
        validators: [(path: string) => ({
            valid: !isExistedPath(path),
            validationMessage: isExistedPath(path)
                ? { id: 'oe.validation.error.pagePathIsNotUnique', defaultMessage: 'The path {pagePath} already exists.' }
                : null
        })]
    }];
};

const validatorsOverrides = {
    plainText: {
        ...plainText,
        required: (...args: string[]): FormVmValueValidatorResultType => {
            const descriptor = { id: 'oe.validation.required.message', defaultMessage: '{fieldName} is required.' };
            const result = requiredValidator(...args);

            if (!result.valid) result.validationMessage = descriptor;

            return result;
        },
    },
    richText: {
        ...richText,
        required: (...args: string[]): FormVmValueValidatorResultType => {
            const descriptor = { id: 'oe.validation.required.message', defaultMessage: '{fieldName} is required.' };
            const result = requiredValidator(...args);

            if (!result.valid) result.validationMessage = descriptor;

            return result;
        },
    },
    meetingId: {
        required: (...args: string[]): FormVmValueValidatorResultType => {
            const descriptor = { id: 'oe.validation.required.message', defaultMessage: '{fieldName} is required.' };
            const result = requiredIdValidator(...args);

            if (!result.valid) result.validationMessage = descriptor;

            return result;
        },
        validInteger: (...args: string[]): FormVmValueValidatorResultType => {
            const descriptor = { id: 'oe.validation.required.message', defaultMessage: '{fieldName} should be valid ID.' };
            const result = requiredIntegerValidator(...args);

            if (!result.valid) result.validationMessage = descriptor;

            return result;
        }
    },
    zipFile: {
        ...file
    }
};

export const config = {
    [PATH]: {
        labelDescriptor: { id: `oe.input.label.${PATH}`, defaultMessage: 'Page Path' },
        defaultValue: '',
        validators: [
            validatorsOverrides.plainText.required,
            validatorsOverrides.plainText.maxLength.short,
            pagePathSymbolsValidator,
            reservedPathValidator
        ],
        formatters: [trimSpaces]
    },
    [LABEL]: {
        labelDescriptor: { id: `oe.input.label.${LABEL}`, defaultMessage: 'Page Label' },
        defaultValue: '',
        validators: [validatorsOverrides.plainText.required, validatorsOverrides.plainText.maxLength.short],
        formatters: [trimSpacesLeft]
    },
    [ENABLED]: {
        defaultValue: true,
        validators: [],
        formatters: []
    },
    [PUBLIC_ENABLED]: {
        defaultValue: false,
        validators: [],
        formatters: []
    },
    /* KNOVIO */
    [EMBED_CODE_ID]: {
        labelDescriptor: { id: `oe.input.label.${EMBED_CODE_ID}`, defaultMessage: `${KnovioLabel.Project} Embed Code Id` },
        defaultValue: '',
        validators: [validatorsOverrides.plainText.required, validatorsOverrides.plainText.maxLength.short],
        formatters: [trimSpaces]
    },
    /* EMBEDDED WEB PAGE */
    [EMBEDDED_WEBPAGE_URL]: {
        labelDescriptor: { id: `oe.input.label.${EMBEDDED_WEBPAGE_URL}`, defaultMessage: 'Embedded URL' },
        defaultValue: '',
        validators: [validatorsOverrides.plainText.required, validatorsOverrides.plainText.maxLength.medium, urlValidator],
        formatters: [trimSpaces]
    },
    /* RICH TEXT PAGE */
    [RICH_TEXT]: {
        labelDescriptor: { id: `oe.input.label.${RICH_TEXT}`, defaultMessage: 'Rich Text' },
        defaultValue: '',
        validators: [validatorsOverrides.richText.required, validatorsOverrides.richText.maxLength.long],
        formatters: [trimSpacesLeft]
    },
    /* SINGLE MEETING PAGE */
    [MEETING_ID]: {
        labelDescriptor: { id: `oe.input.label.${MEETING_ID}`, defaultMessage: 'Meeting ID' },
        defaultValue: '',
        validators: [validatorsOverrides.meetingId.required, validatorsOverrides.meetingId.validInteger],
    },
    /* EMBEDDED HTML PAGE */
    [EMBEDDED_ZIP_FILE]: {
        labelDescriptor: { id: `oe.input.label.${EMBEDDED_ZIP_FILE}`, defaultMessage: 'Embedded Zip File' },
        defaultValue: '',
        validators: [validatorsOverrides.zipFile.required, validatorsOverrides.zipFile.maxSize.zip, validatorsOverrides.zipFile.fileType.zip]
    },
    [EMBEDDED_HTML_FILE_OBJECT_KEY]: {
        labelDescriptor: { id: `oe.input.label.${EMBEDDED_ZIP_FILE}`, defaultMessage: 'Embedded Zip File' },
        defaultValue: '',
        validators: [validatorsOverrides.plainText.required]
    },
    /* SHOWCASE */
    [USER_SHOWCASE]: {
        labelDescriptor: {id: `oe.input.label.${USER_SHOWCASE}`, defaultMessage: 'Showcase Embed Code ID'},
        defaultValue: '',
        validators: [validatorsOverrides.plainText.required, validatorsOverrides.plainText.maxLength.short],
        formatters: [trimSpaces]
    },
    /* EXTERNAL LINK */
    [EXTERNAL_LINK_URL]: {
        labelDescriptor: { id: `oe.input.label.${EXTERNAL_LINK_URL}`, defaultMessage: 'External Link URL' },
        defaultValue: '',
        validators: [validatorsOverrides.plainText.required, validatorsOverrides.plainText.maxLength.medium, urlValidator],
        formatters: [trimSpaces]
    },
    [RESTRICT_ACCESS_ENABLED]: {
        defaultValue: false,
        validators: [],
        formatters: []
    },
    [RESTRICT_ACCESS_FILTERS]: {
        defaultValue: [],
        validators: [],
        formatters: []
    },
    [PAGE_LINK_ENABLED]: {
        defaultValue: false,
        validators: [],
        formatters: []
    },
    [LINK_DESCRIPTION]: {
        labelDescriptor: { id: `oe.input.label.${LINK_DESCRIPTION}`, defaultMessage: '{prefix} Description' },
        defaultValue: '',
        validators: [validatorsOverrides.plainText.maxLength.medium],
        formatters: [trimSpacesLeft]
    },
    [PRESENTER_GROUPS_ENABLED]: {
        defaultValue: false,
        validators: [],
        formatters: []
    },
    [PRESENTER_GROUPS_FILTERS]: {
        defaultValue: [],
        validators: [],
        formatters: []
    },
    [PRESENTER_GROUPS]: {
        defaultValue: [],
        validators: [],
        formatters: []
    },
};

export const defaultPagesConfig = {
    [LABEL]: {
        labelDescriptor: { id: `oe.input.pageLabel.${LABEL}`, defaultMessage: '{prefix} Page Label' },
        defaultValue: '',
        validators: [validatorsOverrides.plainText.required, validatorsOverrides.plainText.maxLength.short],
        formatters: [trimSpacesLeft]
    },
    [SUPPORT_INFORMATION]: {
        labelDescriptor: { id: `oe.input.label.${SUPPORT_INFORMATION}`, defaultMessage: 'Support Information' },
        defaultValue: '',
        validators: [validatorsOverrides.richText.maxLength.long],
        formatters: [trimSpacesLeft]
    },
    [LINK_DESCRIPTION]: {
        labelDescriptor: { id: `oe.input.label.${LINK_DESCRIPTION}`, defaultMessage: '{prefix} Description' },
        defaultValue: '',
        validators: [validatorsOverrides.plainText.maxLength.medium],
        formatters: [trimSpacesLeft],
        fieldInfoMessage: linkDescriptionInfoMessages
    },
    [LAYOUT]: {
        labelDescriptor: { id: `oe.input.label.${LAYOUT}`, defaultMessage: 'General Sessions Layout' },
        defaultValue: '',
        validators: [],
        formatters: []
    },
    [DEFAULT_CAROUSEL_VIEW]: {
        labelDescriptor: { id: `oe.input.label.${DEFAULT_CAROUSEL_VIEW}`, defaultMessage: 'Default Configuration' },
        defaultValue: DefaultCarouselView.Collapsed,
        validators: [],
        formatters: []
    },
    [TOPIC_ENABLED]: {
        defaultValue: false,
        validators: [],
        formatters: []
    },
    [WELCOME_KNOVIO_ENABLED]: {
        defaultValue: false,
        validators: [],
        formatters: []
    },
    [CALENDAR_BUTTON_ENABLED]: {
        defaultValue: false,
        validators: [],
        formatters: []
    },
    [WELCOME_KNOVIO_EMBED_ID]: {
        labelDescriptor: { id: `oe.input.label.${WELCOME_KNOVIO_EMBED_ID}`, defaultMessage: 'Embed Code Id' },
        defaultValue: '',
        validators: [validatorsOverrides.plainText.maxLength.short],
        formatters: [trimSpaces]
    },
    /* SHOWCASE */
    [EMBED_CODE_ID]: {
        labelDescriptor: { id: `oe.input.label.${EMBED_CODE_ID}`, defaultMessage: 'Showcase Embed Code Id' },
        defaultValue: '',
        validators: [validatorsOverrides.plainText.maxLength.short],
        formatters: [trimSpaces]
    },
    [PRE_REGISTRATION_ENABLED]: {
        defaultValue: false,
        validators: [],
        formatters: []
    },
    [MY_SCHEDULE_NOTES]: {
        labelDescriptor: { id: `oe.input.label.${MY_SCHEDULE_NOTES}`, defaultMessage: 'My Schedule Notes' },
        defaultValue: '',
        validators: [validatorsOverrides.richText.maxLength.short],
        formatters: [trimSpacesLeft],
        toggleInfoMessage: myScheduleNotesInfoMessage
    },
    [SORT_ORGANIZATION_BY]: {
        labelDescriptor: { id: `oe.input.label.${SORT_ORGANIZATION_BY}`, defaultMessage: 'Sort Organizations By' },
        defaultValue: SortOrganizationsByOptions.ALPHABETICAL_ORDER,
        validators: [],
        formatters: [],
        toggleInfoMessage: sortOrganizationsByInfoMessages
    },
    [HOST_ATTENDEES_ENABLED]: {
        defaultValue: false,
        validators: [],
        formatters: [],
    },
    [REPLAYS_NOTES]: {
        labelDescriptor: { id: `oe.input.label.${REPLAYS_NOTES}`, defaultMessage: 'Replays Notes' },
        defaultValue: '',
        validators: [validatorsOverrides.richText.maxLength.short],
        formatters: [trimSpacesLeft],
        toggleInfoMessage: replaysNotesInfoMessage
    },
    [REPLAYS_LAYOUT]: {
        labelDescriptor: { id: `oe.input.label.${REPLAYS_LAYOUT}`, defaultMessage: 'Replays Layout' },
        defaultValue: '',
        validators: [],
        formatters: []
    },
    [REPLAYS_DEFAULT_CAROUSEL_VIEW]: {
        labelDescriptor: { id: `oe.input.label.${REPLAYS_DEFAULT_CAROUSEL_VIEW}`, defaultMessage: 'Default Configuration' },
        defaultValue: DefaultCarouselView.ShowAll,
        validators: [],
        formatters: []
    },
    [UPCOMING_SESSIONS_ENABLED]: {
        defaultValue: false,
        validators: [],
        formatters: [],
    },
    [UPCOMING_SESSIONS_LABEL]: {
        labelDescriptor: { id: `oe.input.label.${UPCOMING_SESSIONS_LABEL}`, defaultMessage: 'Upcoming Sessions section label' },
        defaultValue: 'Upcoming Sessions',
        validators: [validatorsOverrides.plainText.required, validatorsOverrides.plainText.maxLength.short],
        formatters: []
    },
    [UPCOMING_SESSIONS_TYPE]: {
        labelDescriptor: { id: `oe.input.label.${UPCOMING_SESSIONS_TYPE}`, defaultMessage: 'Replays Layout' },
        defaultValue: '',
        validators: [],
        formatters: []
    },
    [REPLAYS_SESSION_LABEL]: {
        labelDescriptor: { id: `oe.input.label.${REPLAYS_SESSION_LABEL}`, defaultMessage: 'Replays section label' },
        defaultValue: 'Replays',
        validators: [validatorsOverrides.plainText.required, validatorsOverrides.plainText.maxLength.short],
        formatters: []
    },
    [REPLAYS_PRE_REGISTRATION_ENABLED]: {
        defaultValue: false,
        validators: [],
        formatters: []
    },
    [RESTRICT_ACCESS_ENABLED]: {
        defaultValue: false,
        validators: [],
        formatters: []
    },
    [RESTRICT_ACCESS_FILTERS]: {
        defaultValue: [],
        validators: [],
        formatters: []
    },
    [SUBSCRIBE_ATTENDEE_TO_MEETING_ON_REGISTRATION_ENABLED]: {
        defaultValue: false,
        validators: [],
        formatters: []
    },

    [GENERAL_SESSIONS_NOTES]: {
        labelDescriptor: { id: `oe.input.label.${GENERAL_SESSIONS_NOTES}`, defaultMessage: 'General Sessions Notes' },
        defaultValue: '',
        validators: [validatorsOverrides.richText.maxLength.short],
        formatters: [trimSpacesLeft],
        toggleInfoMessage: generalSessionsNotesInfoMessage
    },
    [PAST_SESSIONS_ENABLED]: {
        defaultValue: true,
        validators: [],
        formatters: []
    },
    [REGISTER_BUTTON_LABEL]: {
        labelDescriptor: { id: `oe.input.label.${REGISTER_BUTTON_LABEL}`, defaultMessage: 'Register Button Label' },
        defaultValue: 'Register',
        validators: [validatorsOverrides.plainText.required, validatorsOverrides.plainText.maxLength.short],
        formatters: [trimSpacesLeft]
    }
};

export const toConfigList = (configObject: Record<string, Omit<FormVmItemConfigType, 'name'>>): FormVmItemConfigType[] =>
    [ ...Object.entries(configObject || {}) ]
        .map(([key, cnf]) => ({ ...cnf, name: key }))
;

export const getPassportPageVmConfig = (configOverrides?: FormVmItemConfigType[]): FormVmItemConfigType[] => {
    const _config = toConfigList(config);

    if (Array.isArray(configOverrides)) {
        configOverrides.forEach(cnf => {
            const idx = _config.findIndex(_cnf => _cnf.name === cnf.name);
            if (idx > -1) {
                _config[idx].validators = (_config[idx]?.validators ?? []).concat(cnf?.validators ?? []);
                _config[idx].formatters = (_config[idx]?.formatters ?? []).concat(cnf?.formatters ?? []);
            }
        });
    }

    return _config;
};